import { useEffect, useState, useCallback } from "react";
import { Toaster, toast } from "sonner";
import { apiFetch } from "../lib/api";

const STATUSES = ["NEW", "CONTACTED", "QUALIFIED", "HOT", "APPOINTMENT PENDING", "FOLLOW-UP DUE", "APPOINTMENT / MEETING / ORDER", "ATTENDED / COMPLETED", "CONVERTED", "DORMANT", "LOST"];
const QUICK_STATUSES = ["all", "HOT", "NEW", "APPOINTMENT PENDING", "FOLLOW-UP DUE", "CONVERTED", "LOST"];
const VERTICALS = ["all", "dental", "consulting", "creative"];

const waReply = (l) => {
  const phone = (l.phone || "").replace(/[^\d]/g, "");
  if (!phone) return "";
  const appt = l.preferred_date ? ` untuk ${l.enquiry_type} pada ${l.preferred_date}${l.preferred_time ? `, ${l.preferred_time}` : ""}` : "";
  const text = l.vertical === "dental"
    ? `Hi ${l.name}, ini pasukan Klinik Pergigian Irma Ariandi. Kami terima appointment request anda${appt}. Boleh kami sahkan slot ini? Terima kasih.`
    : `Hi ${l.name}, thank you for your enquiry to iINSPIRE. Following up personally on "${l.enquiry_type}" — is now a good time to talk?`;
  return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [vertical, setVertical] = useState("all");
  const [status, setStatus] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    apiFetch(`/admin/leads?vertical=${vertical}`)
      .then((data) => {
        setLeads(
          status === "all"
            ? data
            : status === "HOT"
              ? data.filter((l) => l.status === "HOT" || l.intent_level === "HOT")
              : data.filter((l) => l.status === status)
        );
      })
      .catch(() => {});
  }, [vertical, status]);

  useEffect(load, [load]);

  const updateStatus = async (id, newStatus) => {
    await apiFetch(`/admin/leads/${id}`, { method: "PATCH", body: JSON.stringify({ status: newStatus }) });
    load();
  };

  const saveDetails = async (lead) => {
    await apiFetch(`/admin/leads/${lead.id}`, {
      method: "PATCH",
      body: JSON.stringify({
        notes: lead.notes || "",
        owner: lead.owner || "",
        follow_up_date: lead.follow_up_date || "",
        revenue: lead.revenue === "" || lead.revenue == null ? null : Number(lead.revenue),
        lost_reason: lead.lost_reason || "",
        conversion_type: lead.conversion_type || "",
      }),
    });
    toast.success("Lead updated");
    load();
  };

  const updateLocal = (id, key, value) => {
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, [key]: value } : l)));
  };

  const INTENT_STYLE = { HOT: "bg-red-100 text-red-700", HIGH: "bg-amber/15 text-amber", MEDIUM: "bg-softgrey text-ink/60", LOW: "bg-softgrey text-ink/40" };

  return (
    <div data-testid="admin-leads">
      <Toaster position="top-center" richColors />
      <p className="eyebrow">Leads</p>
      <h1 className="mt-2 font-serif text-3xl tracking-tight text-ink">Central lead database</h1>
      <div className="mt-8 flex flex-wrap gap-2">
        {VERTICALS.map((v) => (
          <button
            key={v}
            onClick={() => setVertical(v)}
            data-testid={`leads-filter-${v}`}
            className={`border px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] ${vertical === v ? "border-burgundy bg-burgundy/5 text-burgundy" : "border-ink/15 text-ink/60"}`}
          >
            {v}
          </button>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="leads-status-pills">
        {QUICK_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`leads-status-${s.toLowerCase().replace(/\s+/g, "-")}`}
            className={`px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] transition-colors ${status === s ? "bg-ink text-warmwhite" : "border border-ink/15 text-ink/60 hover:border-amber"}`}
          >
            {s === "all" ? "All" : s}
          </button>
        ))}
      </div>

      {leads.length === 0 ? (
        <p className="mt-10 text-sm text-ink/50" data-testid="leads-empty">No leads yet. New enquiries appear here instantly.</p>
      ) : (
        <div className="mt-8 space-y-3">
          {leads.map((l) => (
            <div key={l.id} className="border border-ink/10 bg-warmwhite" data-testid={`lead-row-${l.id.slice(0, 8)}`}>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-3 p-5">
                <div className="min-w-40">
                  <p className="font-medium text-ink">{l.name}</p>
                  <p className="text-xs text-ink/50">{l.phone || "no phone"} · {new Date(l.updated_at || l.created_at).toLocaleDateString()}</p>
                </div>
                <span className="bg-softgrey px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.15em] text-ink/70">{l.vertical}</span>
                <p className="text-sm text-ink/75">{l.enquiry_type}</p>
                {(l.preferred_date || l.preferred_time) && (
                  <span className="px-2 py-1 text-[10px] font-semibold text-burgundy" data-testid={`lead-appt-${l.id.slice(0, 8)}`}>
                    {l.preferred_date} {l.preferred_time}
                  </span>
                )}
                {waReply(l) && (
                  <a
                    href={waReply(l)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`lead-whatsapp-${l.id.slice(0, 8)}`}
                    className="border border-[#25D366]/50 bg-[#25D366]/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-ink transition-colors hover:bg-[#25D366] hover:text-white"
                  >
                    Open WhatsApp
                  </a>
                )}
                {l.intent_level && (
                  <span
                    className={`px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.15em] ${INTENT_STYLE[l.intent_level] || INTENT_STYLE.LOW}`}
                    title={(l.score_reasons || []).join(" • ")}
                    data-testid={`lead-intent-${l.id.slice(0, 8)}`}
                  >
                    {l.intent_level}
                  </span>
                )}
                <select
                  value={l.status}
                  onChange={(e) => updateStatus(l.id, e.target.value)}
                  data-testid={`lead-status-${l.id.slice(0, 8)}`}
                  className="ml-auto border border-ink/15 bg-white px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-ink/70"
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button
                  onClick={() => setExpanded(expanded === l.id ? null : l.id)}
                  className="border border-ink/15 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-ink/60 hover:border-amber"
                  data-testid={`lead-expand-${l.id.slice(0, 8)}`}
                >
                  {expanded === l.id ? "Close" : "Manage"}
                </button>
              </div>
              {expanded === l.id && (
                <div className="border-t border-ink/10 p-5" data-testid={`lead-detail-${l.id.slice(0, 8)}`}>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <p className="text-xs text-ink/60"><span className="font-semibold text-ink/80">Email:</span> {l.email || "—"}</p>
                    <p className="text-xs text-ink/60"><span className="font-semibold text-ink/80">Phone:</span> {l.phone || "—"}</p>
                    <p className="text-xs text-ink/60"><span className="font-semibold text-ink/80">Source:</span> {l.lead_source_id || l.source_page || "—"}</p>
                    <p className="text-xs text-ink/60"><span className="font-semibold text-ink/80">Source page:</span> {l.source_page || "—"}</p>
                    <p className="text-xs text-ink/60"><span className="font-semibold text-ink/80">Appointment:</span> {l.preferred_date || "—"} {l.preferred_time || ""}</p>
                    <p className="text-xs text-ink/60"><span className="font-semibold text-ink/80">Contact method:</span> {l.contact_method || "—"}</p>
                    <p className="text-xs text-ink/60"><span className="font-semibold text-ink/80">WhatsApp handoff:</span> {l.wa_handoff_at ? new Date(l.wa_handoff_at).toLocaleString() : "Not yet"}</p>
                    {(l.utm_source || l.utm_campaign || l.gclid) && (
                      <p className="text-xs text-ink/60 sm:col-span-2 lg:col-span-3">
                        <span className="font-semibold text-ink/80">Attribution:</span> {[l.utm_source, l.utm_medium, l.utm_campaign, l.gclid && `gclid:${l.gclid}`].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    {l.score_reasons?.length > 0 && (
                      <p className="text-xs text-ink/60 sm:col-span-2 lg:col-span-3">
                        <span className="font-semibold text-ink/80">Why this score:</span> {l.score_reasons.join(" • ")}
                      </p>
                    )}
                    {l.conversation_summary && (
                      <p className="text-xs leading-relaxed text-ink/60 sm:col-span-2 lg:col-span-3">
                        <span className="font-semibold text-ink/80">Conversation:</span> {l.conversation_summary}
                      </p>
                    )}
                    <p className="text-xs leading-relaxed text-ink/60 sm:col-span-2 lg:col-span-3">
                      <span className="font-semibold text-ink/80">Message:</span> {l.message}
                    </p>
                    <label className="block">
                      <span className="mb-1 block text-[10px] uppercase tracking-[0.15em] text-ink/50">Owner</span>
                      <input value={l.owner || ""} onChange={(e) => updateLocal(l.id, "owner", e.target.value)} className="w-full border border-ink/20 bg-white px-2 py-1.5 text-xs" data-testid={`lead-owner-${l.id.slice(0, 8)}`} />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-[10px] uppercase tracking-[0.15em] text-ink/50">Follow-up date</span>
                      <input type="date" value={l.follow_up_date || ""} onChange={(e) => updateLocal(l.id, "follow_up_date", e.target.value)} className="w-full border border-ink/20 bg-white px-2 py-1.5 text-xs" data-testid={`lead-followup-${l.id.slice(0, 8)}`} />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-[10px] uppercase tracking-[0.15em] text-ink/50">Revenue (RM)</span>
                      <input type="number" value={l.revenue ?? ""} onChange={(e) => updateLocal(l.id, "revenue", e.target.value)} className="w-full border border-ink/20 bg-white px-2 py-1.5 text-xs" data-testid={`lead-revenue-${l.id.slice(0, 8)}`} />
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="mb-1 block text-[10px] uppercase tracking-[0.15em] text-ink/50">Notes</span>
                      <input value={l.notes || ""} onChange={(e) => updateLocal(l.id, "notes", e.target.value)} className="w-full border border-ink/20 bg-white px-2 py-1.5 text-xs" data-testid={`lead-notes-${l.id.slice(0, 8)}`} />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-[10px] uppercase tracking-[0.15em] text-ink/50">Lost reason</span>
                      <input value={l.lost_reason || ""} onChange={(e) => updateLocal(l.id, "lost_reason", e.target.value)} className="w-full border border-ink/20 bg-white px-2 py-1.5 text-xs" data-testid={`lead-lost-${l.id.slice(0, 8)}`} />
                    </label>
                  </div>
                  <button onClick={() => saveDetails(l)} className="btn-primary mt-5 !px-5 !py-2.5" data-testid={`lead-save-${l.id.slice(0, 8)}`}>
                    Save
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
